import { analyticsService } from './AnalyticsService';
import { TIER_1_EVENTS, type AnalyticsEventData } from './EventTypes';
import { User } from '@supabase/supabase-js';

export class Tier1Tracking {
  // Generic trackEvent method with enhanced logging and forced flush
  static async trackEvent(
    eventType: string,
    eventCategory: string,
    eventData?: Record<string, any>,
    user?: User | null
  ) {
    console.log(`[Tier1 Analytics] === TRACKING EVENT ===`);
    console.log(`[Tier1 Analytics] Event Type: ${eventType}`);
    console.log(`[Tier1 Analytics] Event Category: ${eventCategory}`);
    console.log(`[Tier1 Analytics] Event Data:`, eventData);
    console.log(`[Tier1 Analytics] User:`, user ? `${user.id} (${user.email})` : 'None');
    
    try {
      // Call trackEvent and wait for it
      await analyticsService.trackEvent(eventType, eventCategory, eventData, user);
      
      // Force immediate flush for all Tier1 events
      console.log(`[Tier1 Analytics] About to force flush...`);
      await analyticsService.forceFlush();
      console.log(`[Tier1 Analytics] === EVENT TRACKED AND FLUSHED SUCCESSFULLY ===`);
    } catch (error) {
      console.error(`[Tier1 Analytics] === TRACKING FAILED ===`);
      console.error(`[Tier1 Analytics] Error:`, error);
      throw error;
    }
  }

  // Content Saving Tracking - FIXED to use proper category
  static async trackContentSaved(
    contentId: string,
    sourceMethod: 'manual' | 'extension' | 'bookmark_import',
    contentType: string,
    url?: string,
    title?: string,
    description?: string,
    user?: User | null
  ) {
    const domain = url ? new URL(url).hostname : undefined;
    
    const eventData: AnalyticsEventData = {
      content_saved: {
        content_id: contentId,
        source_method: sourceMethod,
        content_type: contentType,
        url_domain: domain,
        has_title: Boolean(title?.trim()),
        has_description: Boolean(description?.trim()),
      }
    };

    let eventType: string;
    switch (sourceMethod) {
      case 'extension':
        eventType = TIER_1_EVENTS.CONTENT_SAVED_EXTENSION;
        break;
      case 'bookmark_import':
        eventType = TIER_1_EVENTS.CONTENT_SAVED_IMPORT;
        break;
      default:
        eventType = TIER_1_EVENTS.CONTENT_SAVED_MANUAL;
    }

    // CRITICAL: Use 'content_saving' category (not 'user_action')
    console.log(`[Tier1 Analytics] Content saved via ${sourceMethod}:`, contentId);
    await this.trackEvent(eventType, 'content_saving', eventData, user);
  }

  // Organization & Curation Tracking - FIXED to use proper categories
  static async trackCollectionCreated(collectionId: string, name: string, color: string, user?: User | null) {
    const eventData: AnalyticsEventData = {
      collection_created: { collection_id: collectionId, name, color }
    };
    
    console.log(`[Tier1 Analytics] Collection created:`, name);
    await this.trackEvent(TIER_1_EVENTS.COLLECTION_CREATED, 'organization', eventData, user);
  }

  static async trackTagCreated(tagId: string, name: string, color: string, user?: User | null) {
    console.log(`[Tier1 Analytics] === TRACK TAG CREATED CALLED ===`);
    console.log(`[Tier1 Analytics] Tag ID: ${tagId}`);
    console.log(`[Tier1 Analytics] Tag Name: ${name}`);
    console.log(`[Tier1 Analytics] Tag Color: ${color}`);
    console.log(`[Tier1 Analytics] User: ${user ? `${user.id} (${user.email})` : 'None'}`);
    
    const eventData: AnalyticsEventData = {
      tag_created: { tag_id: tagId, name, color }
    };
    
    console.log(`[Tier1 Analytics] Event data structure:`, eventData);
    console.log(`[Tier1 Analytics] Event type: ${TIER_1_EVENTS.TAG_CREATED}`);
    console.log(`[Tier1 Analytics] Event category: organization`);
    
    try {
      await this.trackEvent(TIER_1_EVENTS.TAG_CREATED, 'organization', eventData, user);
      console.log(`[Tier1 Analytics] === TAG CREATION TRACKING COMPLETED ===`);
    } catch (error) {
      console.error(`[Tier1 Analytics] === TAG CREATION TRACKING FAILED ===`);
      console.error(`[Tier1 Analytics] Error details:`, error);
      throw error;
    }
  }

  static async trackBoardCreated(boardId: string, name: string, color: string, user?: User | null) {
    const eventData: AnalyticsEventData = {
      board_created: { board_id: boardId, name, color }
    };
    
    console.log(`[Tier1 Analytics] Board created:`, name);
    await this.trackEvent(TIER_1_EVENTS.BOARD_CREATED, 'organization', eventData, user);
  }

  static async trackContentOrganized(
    contentId: string,
    action: 'added_to_collection' | 'tagged' | 'added_to_board' | 'removed_from_collection' | 'untagged' | 'removed_from_board',
    targetId: string,
    targetType: 'collection' | 'tag' | 'board',
    user?: User | null
  ) {
    console.log(`[Tier1 Analytics] === CONTENT ORGANIZED TRACKING START ===`);
    console.log(`[Tier1 Analytics] Content ID: ${contentId}`);
    console.log(`[Tier1 Analytics] Action: ${action}`);
    console.log(`[Tier1 Analytics] Target ID: ${targetId}`);
    console.log(`[Tier1 Analytics] Target Type: ${targetType}`);
    console.log(`[Tier1 Analytics] User: ${user ? `${user.id} (${user.email})` : 'None'}`);

    const eventData: AnalyticsEventData = {
      content_organized: { 
        content_id: contentId, 
        action, 
        target_id: targetId, 
        target_type: targetType 
      }
    };

    let eventType: string;
    if (action.includes('collection')) {
      eventType = TIER_1_EVENTS.CONTENT_ADDED_TO_COLLECTION;
    } else if (action.includes('tag')) {
      eventType = TIER_1_EVENTS.CONTENT_TAGGED;
    } else {
      eventType = TIER_1_EVENTS.CONTENT_ADDED_TO_BOARD;
    }

    console.log(`[Tier1 Analytics] Using event type: ${eventType}`);
    console.log(`[Tier1 Analytics] Event data:`, eventData);

    try {
      // Use 'organization' category for content organization
      await this.trackEvent(eventType, 'organization', eventData, user);
      console.log(`[Tier1 Analytics] === CONTENT ORGANIZED TRACKING COMPLETED ===`);
    } catch (error) {
      console.error(`[Tier1 Analytics] === CONTENT ORGANIZED TRACKING FAILED ===`);
      console.error(`[Tier1 Analytics] Error:`, error);
      throw error;
    }
  }

  static async trackContentEdited(contentId: string, fieldsChanged: string[], user?: User | null) {
    console.log(`[Tier1 Analytics] === CONTENT EDITED TRACKING START ===`);
    console.log(`[Tier1 Analytics] Content ID: ${contentId}`);
    console.log(`[Tier1 Analytics] Fields Changed: ${fieldsChanged.join(', ')}`);
    console.log(`[Tier1 Analytics] User: ${user ? `${user.id} (${user.email})` : 'None'}`);

    const eventData: AnalyticsEventData = {
      content_edited: { 
        content_id: contentId, 
        fields_changed: fieldsChanged,
        edit_frequency_score: fieldsChanged.length 
      }
    };
    
    console.log(`[Tier1 Analytics] Event data:`, eventData);

    try {
      await this.trackEvent(TIER_1_EVENTS.CONTENT_EDITED, 'curation', eventData, user);
      console.log(`[Tier1 Analytics] === CONTENT EDITED TRACKING COMPLETED ===`);
    } catch (error) {
      console.error(`[Tier1 Analytics] === CONTENT EDITED TRACKING FAILED ===`);
      console.error(`[Tier1 Analytics] Error:`, error);
      throw error;
    }
  }

  // Content Discovery Tracking
  static async trackNavigation(
    navigationType: 'collection' | 'tag' | 'board' | 'inbox' | 'archive',
    targetId?: string,
    targetName?: string,
    contentCount?: number,
    user?: User | null
  ) {
    const eventData: AnalyticsEventData = {
      navigation_used: { 
        navigation_type: navigationType, 
        target_id: targetId, 
        target_name: targetName,
        content_count: contentCount 
      }
    };

    let eventType: string;
    switch (navigationType) {
      case 'collection':
        eventType = TIER_1_EVENTS.NAVIGATION_COLLECTION;
        break;
      case 'tag':
        eventType = TIER_1_EVENTS.NAVIGATION_TAG;
        break;
      case 'board':
        eventType = TIER_1_EVENTS.NAVIGATION_BOARD;
        break;
      default:
        eventType = TIER_1_EVENTS.NAVIGATION_COLLECTION;
    }

    console.log(`[Tier1 Analytics] Navigation used:`, navigationType, targetName);
    await this.trackEvent(eventType, 'discovery', eventData, user);
  }

  static async trackSearch(searchTerm: string, resultsCount: number, user?: User | null) {
    const eventData: AnalyticsEventData = {
      search_used: { 
        search_term: searchTerm, 
        results_count: resultsCount,
        search_duration_ms: Date.now()
      }
    };
    
    console.log(`[Tier1 Analytics] Search performed:`, searchTerm, `${resultsCount} results`);
    await this.trackEvent(TIER_1_EVENTS.SEARCH_PERFORMED, 'discovery', eventData, user);
  }

  static async trackSearchResultClicked(searchTerm: string, contentId: string, user?: User | null) {
    const eventData: AnalyticsEventData = {
      search_used: { 
        search_term: searchTerm, 
        results_count: 0,
        result_clicked: true 
      }
    };
    
    console.log(`[Tier1 Analytics] Search result clicked:`, contentId);
    await this.trackEvent(TIER_1_EVENTS.SEARCH_RESULT_CLICKED, 'discovery', eventData, user);
  }

  // Browser Extension Detection
  static detectBrowserExtension(): 'manual' | 'extension' {
    const hasExtensionIndicators = (
      window.location.search.includes('ext=true') ||
      document.referrer.includes('extension://') ||
      window.navigator.userAgent.includes('ClustrExtension')
    );
    
    return hasExtensionIndicators ? 'extension' : 'manual';
  }
}
